import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import WarningMessage from "../edit_doc/WarningMessage";
import ApparatModeWarning from "../edit_doc/ApparatModeWarning";
import { fetchBlankDocument } from "../../store/actions/documentsActions";
import {
    createPaymentAction,
    fetchPriceApparat,
} from "../../store/actions/paymentsActions";
import { useKioskToast, KioskToast } from "../UI/elements/useKioskToast";

// Заказ чистых листов A4 — отдельная услуга без печати документа.
// Экран 5 редизайна в режиме .set-wrap.blank-mode: лист-мокап со счётчиком поверх него.
//
// Документ — системная строка на бэкенде (GET /api/v1/documents/blank,
// kind="blank"), создаётся один раз лениво и переиспользуется всеми заказами.
// Цена — priceApparat.price_for_blank_sheet (см. /api/v1/payments/price).
const MAX_SHEETS = 15;
const FALLBACK_PRICE = 30; // пока priceApparat ещё не загрузился

const blockVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.45, delay: i * 0.1 },
    }),
};

function BlankSheet() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [sheets, setSheets] = useState(1);
    // null = ответ ещё не пришёл (или запрос упал) — состояние "неизвестно",
    // отдельное от "бумаги 0". Раньше оба случая жили в одном числе, из-за
    // чего упавший запрос молча открывал лимит в MAX_SHEETS при пустом лотке.
    const [availablePapers, setAvailablePapers] = useState(null);
    const { toastMsg, showToast } = useKioskToast();

    const { blankDocument } = useSelector((state) => state.documentsList);
    const { priceApparat, loadingPayment } = useSelector((state) => state.payments);

    useEffect(() => {
        dispatch(fetchBlankDocument());
        dispatch(fetchPriceApparat());
    }, [dispatch]);

    // Тот же источник остатка бумаги, что и на экране параметров печати.
    useEffect(() => {
        import("axios").then(({ default: axios }) => {
            const deviceToken = localStorage.getItem("deviceToken");
            axios
                .get(
                    `${process.env.REACT_APP_BASEURL}/api/v1/admin/apparats/pages`,
                    {
                        headers: { "Device-Token": `${deviceToken}` },
                    },
                )
                .then((response) =>
                    // max_tray_pages — максимум среди лотков, см. комментарий
                    // в EditDoc.jsx. Чистый лист — всегда один документ, поэтому
                    // сравнение с одним лотком здесь корректно без оговорок.
                    setAvailablePapers(
                        response.data.max_tray_pages ??
                            response.data.count_pages ??
                            0,
                    ),
                )
                .catch((error) => {
                    console.error("Error fetching available papers:", error);
                    // availablePapers остаётся null ("неизвестно") — не 0.
                    // Иначе упавший запрос выглядел бы как пустой лоток.
                });
        });
    }, []);

    const papersKnown = availablePapers !== null;
    // Пока не знаем остаток — не блокируем оплату лимитом (последнее слово
    // всё равно за сервером в create_payment), но и не открываем счётчик
    // выше делового потолка MAX_SHEETS.
    const paperLimit = papersKnown ? Math.max(1, availablePapers) : MAX_SHEETS;
    const limit = Math.min(MAX_SHEETS, paperLimit);

    // Если данные о бумаге пришли уже после того, как пользователь поднял
    // счётчик (пока лимит ещё был "неизвестно" = MAX_SHEETS) — подрезаем до
    // реального лимита, а не оставляем цифру, которую нельзя оплатить.
    useEffect(() => {
        if (papersKnown) {
            setSheets((prev) => Math.min(prev, limit));
        }
    }, [papersKnown, limit]);

    const changeSheets = (delta) =>
        setSheets((prev) => Math.min(limit, Math.max(1, prev + delta)));

    const handleIncrement = () => {
        if (sheets < limit) {
            changeSheets(1);
            return;
        }
        // Упёрлись в потолок — молчать нельзя, иначе кнопка выглядит сломанной.
        // Различаем причину: реальная нехватка бумаги vs деловой лимит на заказ.
        if (papersKnown && sheets >= availablePapers) {
            showToast(
                t("editDoc.blank.toast_paper_limit", { count: availablePapers }),
            );
        } else {
            showToast(t("editDoc.blank.toast_max_sheets", { count: MAX_SHEETS }));
        }
    };

    // "Нет бумаги вовсе" и "бумаги меньше, чем выбрано" — оба варианта
    // недостачи, оба должны блокировать оплату и показывать плашку.
    const showWarning =
        papersKnown && (availablePapers <= 0 || availablePapers < sheets);
    // accept_orders — единственный из режимов удалённого управления, который
    // применяется к чистым листам: is_color/allow_duplex/allow_photo_print
    // сервер для тарифа blank не проверяет (см. _reject_if_mode_disabled).
    const ordersPaused = priceApparat?.accept_orders === false;
    const pricePerSheet = priceApparat?.price_for_blank_sheet ?? FALLBACK_PRICE;
    const total = sheets * pricePerSheet;
    const canPay =
        Boolean(blankDocument?.id) && !showWarning && !loadingPayment && !ordersPaused;

    const handlePayment = async () => {
        if (!blankDocument?.id) return;
        try {
            const resultAction = await dispatch(
                createPaymentAction([
                    {
                        document_id: blankDocument.id,
                        amount: total,
                        custom_pages: "",
                        // count_pages — списание бумаги и цена (сервер считает
                        // сумму сам, см. price_for_blank_sheet × count_pages).
                        count_pages: sheets,
                        // copy_count — сколько раз киоск напечатает один и тот
                        // же одностраничный PDF; сервер НЕ домножает на него
                        // сумму для тарифа blank (см. payments_crud.py).
                        copy_count: sheets,
                        is_all_pages: true,
                        is_album_orientation: false,
                        is_one_side: true,
                        // Прямой смысл: false = без цвета. Сервер для тарифа
                        // blank всё равно форсит False (payments_crud.py),
                        // но шлём честно.
                        is_color: false,
                        status: "pending",
                    },
                ]),
            );
            if (createPaymentAction.fulfilled.match(resultAction)) {
                navigate("/payments", {
                    state: { from: "/printing/blank_sheet" },
                });
            } else {
                console.error(
                    "Ошибка создания платежа:",
                    resultAction.payload || t("editDoc.payment_error"),
                );
            }
        } catch (error) {
            console.error(
                "Ошибка при отправке запроса на сервер:",
                error.message,
            );
        }
    };

    return (
        <div className="h-[88vh] px-[2.4vw] py-[2vh] flex flex-col gap-[1.2vh] bg-[#EDEFF4]">
            {/* Лист A4 со счётчиком поверх него */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={0}
                className="flex-1 min-h-0 bg-white rounded-[3vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] px-[1.4vw] py-[1.6vh] flex flex-col items-center gap-[1.2vh]"
            >
                <span className="self-start flex-none text-[1.1vh] font-bold uppercase tracking-[0.15em] text-[#7B8290]">
                    {t("editDoc.preview_label")}
                </span>

                <div className="flex-1 min-h-0 flex items-center justify-center">
                    {/* пропорция A4 1:√2 задаётся высотой, ширина считается от неё */}
                    <div className="h-full aspect-[1/1.414] max-w-full bg-white border-[0.25vh] border-[#E1E4EB] rounded-[1.2vh] flex items-center justify-center">
                        {/* relative — якорь для тоста лимита бумаги, всплывает
                            прямо над счётчиком, а не над всем мокапом A4 */}
                        <div className="relative flex items-center gap-[2vw]">
                            <KioskToast message={toastMsg} position="bottom" />
                            <button
                                type="button"
                                onClick={() => changeSheets(-1)}
                                disabled={sheets <= 1}
                                className="w-[9vh] h-[9vh] rounded-[2.6vh] bg-[#F1F3F7] text-[4.4vh] font-bold leading-none shadow-[0_0.6vh_2vh_rgba(18,22,31,0.08)] disabled:opacity-30 active:bg-[#246CE2] active:text-white transition-colors"
                            >
                                −
                            </button>
                            <div className="min-w-[12vh] text-center">
                                <span className="block text-[7.4vh] font-extrabold tracking-tight leading-none text-[#246CE2]">
                                    {sheets}
                                </span>
                                <small className="block text-[1.5vh] font-bold text-[#7B8290] mt-[0.4vh]">
                                    {t("editDoc.blank.sheets")}
                                </small>
                            </div>
                            <button
                                type="button"
                                onClick={handleIncrement}
                                aria-disabled={sheets >= limit}
                                // Не ставим disabled: кнопка на потолке должна
                                // оставаться нажимаемой, чтобы показать тост
                                // с причиной — иначе выглядит просто сломанной.
                                className={`w-[9vh] h-[9vh] rounded-[2.6vh] bg-[#F1F3F7] text-[4.4vh] font-bold leading-none shadow-[0_0.6vh_2vh_rgba(18,22,31,0.08)] active:bg-[#246CE2] active:text-white transition-colors ${sheets >= limit ? "opacity-30" : ""}`}
                            >
                                +
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex-none w-full bg-[#F1F3F7] rounded-[2.2vh] px-[1.8vw] py-[1.2vh] flex items-baseline justify-center gap-[1.2vw] flex-wrap">
                    <span className="text-[1.7vh] font-extrabold">
                        {t("editDoc.blank.title")}
                    </span>
                    <span className="text-[1.35vh] font-semibold text-[#7B8290]">
                        {t("editDoc.blank.note")}
                    </span>
                </div>
            </motion.div>

            <WarningMessage
                showWarning={showWarning}
                availablePapers={availablePapers}
                requiredSheets={sheets}
                t={t}
            />
            <ApparatModeWarning ordersPaused={ordersPaused} photoBlocked={false} t={t} />

            {/* Итог и оплата */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={1}
                className="flex-none bg-white rounded-[3vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] py-[1.4vh] px-[2.4vw] flex items-center gap-[2vw]"
            >
                <div className="min-w-0">
                    <span className="text-[1.1vh] font-bold uppercase tracking-[0.15em] text-[#7B8290]">
                        {t("editDoc.total")}
                    </span>
                    <div className="text-[4.2vh] font-extrabold tracking-tight text-[#246CE2] leading-none mt-[0.4vh]">
                        {total} ₸
                    </div>
                </div>
                <button
                    type="button"
                    onClick={handlePayment}
                    disabled={!canPay}
                    className="ml-auto flex-none flex items-center gap-[0.8vw] bg-[#246CE2] text-white rounded-full py-[1.4vh] px-[2.4vw] text-[1.8vh] font-bold disabled:opacity-40"
                >
                    {t("editDoc.to_payment")}
                    <svg
                        className="w-[2.1vh] h-[2.1vh]"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                </button>
            </motion.div>
        </div>
    );
}

export default BlankSheet;
